import { useState, PropsWithChildren } from "react";

import { Button, CircularProgress, Grid, TextField } from "@material-ui/core";
import { Color, ColorPicker, createColor } from "material-ui-color";

import { MUTATIONS, QUERIES } from "../../../../graphql";
import {
  Apartment,
  CommunicationGroup,
  CommunicationGroupApartment,
  CommunicationGroupData,
  CommunicationGroupUser,
  computeUserVisualName,
  User,
} from "../../../../graphql/models";

import useCommunicationGroupFormStyle from "./communication_group_form.style";

import {
  ApartmentBoxPreview,
  GqlRecordPicker,
  UserListDetail,
} from "../../../components";
import { useSnackbarMutation } from "../../../../hooks";

export interface CommunicationGroupEditFormProps
  extends PropsWithChildren<any> {
  value: CommunicationGroup | CommunicationGroupData | undefined;
  onValueUpdatedSuccessfully: (v: CommunicationGroup) => void;
}

export function CommunicationGroupForm(props: CommunicationGroupEditFormProps) {
  const { value, onValueUpdatedSuccessfully } = props;
  const classes = useCommunicationGroupFormStyle();

  let mutation = MUTATIONS.CREATE_COMMUNICATION_GROUP;
  if (value && (value as CommunicationGroup).id) {
    mutation = MUTATIONS.UPDATE_COMMUNICATION_GROUP;
  }

  const [
    invokeCreateOrUpdateCommunicationGroup,
    { loading, data: createOrUpdateCommunicationGroupData },
  ] = useSnackbarMutation(mutation);
  const [updated, setUpdated] = useState(false);

  const [name, setName] = useState<string>(value?.name ?? "");
  const [description, setDescription] = useState<string>(
    value?.description ?? ""
  );
  const [color, setColor] = useState<Color>(
    value?.color ? createColor(value.color) : createColor("red")
  );
  const [apartments, setApartments] = useState<CommunicationGroupApartment[]>(
    value?.apartments ?? []
  );
  const [users, setUsers] = useState<CommunicationGroupUser[]>(
    value?.users ?? []
  );

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    invokeCreateOrUpdateCommunicationGroup({
      variables: {
        id: (value as CommunicationGroup).id,
        communicationGroupData: {
          name,
          description,
          color: `#${color.hex}`,
          apartments: apartments.map(({ id, code, name }) => ({
            id,
            code,
            name,
          })) as CommunicationGroupApartment[],
          users,
        },
      },
    });
  };

  if (createOrUpdateCommunicationGroupData && !updated) {
    onValueUpdatedSuccessfully(
      createOrUpdateCommunicationGroupData.createSystemCommunicationGroup
    );
    setUpdated(true);
  }

  return (
    <>
      {loading && `Loading...`}
      {!createOrUpdateCommunicationGroupData && (
        <form
          className={classes.root}
          onSubmit={onFormSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={3}>
            <Grid item sm={12} xs={12}>
              <TextField
                required
                className={classes.textField}
                label="Nome"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <TextField
                required
                className={classes.textField}
                label="Descrizione"
                variant="outlined"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <GqlRecordPicker
                className={classes.textField}
                multiple={true}
                value={apartments}
                getOptionLabel={(record: Apartment) =>
                  [
                    record.building?.name,
                    record.code,
                    record.name,
                    record.owner?.name,
                  ]
                    .filter((v) => Boolean(v))
                    .join("-")
                }
                placeHolder={"Appartamenti"}
                renderOption={(record: Apartment) => (
                  <ApartmentBoxPreview apartment={record} />
                )}
                onValueChange={(value) => setApartments(value)}
                query={QUERIES.APARTMENTS}
                queryResultDataField={"apartments"}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <GqlRecordPicker
                className={classes.textField}
                multiple={true}
                value={users}
                getOptionLabel={(record: CommunicationGroupUser | User) => {
                  // console.log("record is", record);
                  if ((record as CommunicationGroupUser)?.fullName) {
                    return (record as CommunicationGroupUser).fullName ?? "";
                  } else {
                    return computeUserVisualName(record as User);
                  }
                }}
                placeHolder={"Utenti"}
                renderOption={(record: User) => (
                  <UserListDetail user={record} />
                )}
                onValueChange={(value: (User | CommunicationGroupUser)[]) => {
                  setUsers(
                    value.map((u) => {
                      if (!(u as CommunicationGroupUser).fullName) {
                        return {
                          id: u.id,
                          username: u.username,
                          fullName: computeUserVisualName(u as User),
                        };
                      }

                      return u as CommunicationGroupUser;
                    })
                  );
                }}
                query={QUERIES.USERS}
                queryResultDataField={"users"}
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <ColorPicker value={color} onChange={setColor} />
            </Grid>
            <Grid item sm={12} xs={12}>
              <Button
                className={classes.textField}
                type="submit"
                variant="contained"
                color="primary"
              >
                {!loading ? "Salva" : <CircularProgress />}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
}
